import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { images, siteInfo } from '@/data/mock';

const navLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'Maine Coon', path: '/maine-coon' },
  { name: 'Notre Élevage', path: '/elevage' },
  { name: 'Livre d\'Or', path: '/livre-dor' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-2'
          : 'bg-transparent py-4'
      }`}
    >
      {/* Top bar with contact info */}
      <div className={`transition-all duration-300 overflow-hidden ${
        isScrolled ? 'h-0 opacity-0' : 'h-auto opacity-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center gap-6 text-sm text-[var(--text-subtle)] pb-2">
            <a
              href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-2 hover:text-[var(--color-gold)] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {siteInfo.phone}
            </a>
            <a
              href={`mailto:${siteInfo.email}`}
              className="flex items-center gap-2 hover:text-[var(--color-gold)] transition-colors"
            >
              <Mail className="w-4 h-4" />
              {siteInfo.email}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={images.logo}
              alt="Maison Bengal & Coon"
              className="h-12 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="font-serif text-xl font-medium text-[var(--text-primary)]">
                Maison Bengal & Coon
              </h1>
              <p className="text-xs text-[var(--text-subtle)]">
                Élevage familial en Bretagne
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm uppercase tracking-wider ${
                  location.pathname === link.path
                    ? 'text-[var(--text-primary)] after:w-full'
                    : ''
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[var(--surface-cream)]">
                <SheetTitle className="font-serif text-xl mb-6">Menu</SheetTitle>
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-[var(--color-beige)] transition-colors ${
                        location.pathname === link.path
                          ? 'text-[var(--color-gold)]'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-[var(--color-beige)]">
                  <a
                    href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--color-gold)] transition-colors mb-4"
                  >
                    <Phone className="w-5 h-5" />
                    {siteInfo.phone}
                  </a>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--color-gold)] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    {siteInfo.email}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};
